"use client";

import * as React from "react";

export type Theme = "system" | "light" | "hybrid" | "dark";

const STORAGE_KEY = "curriva-theme";
const QUERY = "(prefers-color-scheme: dark)";
const CHANGE_EVENT = "curriva-theme-change";

function isTheme(value: string | null): value is Theme {
  return value === "system" || value === "light" || value === "hybrid" || value === "dark";
}

/**
 * Zet de gekozen weergave op <html>. Bij "systeem" volgt hij de voorkeur
 * van het besturingssysteem. Dezelfde logica staat als klein script in de
 * <head> (zie app/layout.tsx), zodat het eerste beeld al goed staat.
 */
function applyTheme(theme: Theme) {
  const dark =
    theme === "dark" ||
    (theme === "system" && window.matchMedia(QUERY).matches);
  document.documentElement.classList.toggle("dark", dark);
  document.documentElement.classList.toggle("hybrid", theme === "hybrid");
}

// localStorage is de bron; `storage` vangt andere tabbladen op, WISSEL de
// eigen. Zo blijft elk menu in de app dezelfde keuze tonen zonder context.
function subscribe(onChange: () => void) {
  window.addEventListener("storage", onChange);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
}

function currentTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  return isTheme(stored) ? stored : "system";
}

export function useTheme() {
  // De server kent de keuze niet en gokt "systeem"; na de hydratatie leest
  // useSyncExternalStore de echte waarde en rendert opnieuw.
  const theme = React.useSyncExternalStore(
    subscribe,
    currentTheme,
    () => "system" as Theme
  );

  const setTheme = React.useCallback((next: Theme) => {
    localStorage.setItem(STORAGE_KEY, next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return { theme, setTheme };
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme } = useTheme();

  React.useEffect(() => {
    applyTheme(theme);
    // Alleen in de systeemstand luisteren we mee met het OS.
    if (theme !== "system") return;
    const mql = window.matchMedia(QUERY);
    const onChange = () => applyTheme("system");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [theme]);

  return children;
}

/** Script dat vóór de eerste paint draait en zo een lichtflits voorkomt. */
export const themeScript = `(function(){try{var t=localStorage.getItem(${JSON.stringify(
  STORAGE_KEY
)});if(t!=="light"&&t!=="hybrid"&&t!=="dark")t="system";if(t==="hybrid")document.documentElement.classList.add("hybrid");else if(t==="dark"||(t==="system"&&window.matchMedia(${JSON.stringify(
  QUERY
)}).matches))document.documentElement.classList.add("dark")}catch(e){}})()`;
